import type { FC, ReactNode, Dispatch, SetStateAction } from "react";
import React, { useContext } from "react";
import type { FeedbackMessageType } from "../../types/undoRedo.types";
import type { Dependency } from "../../hooks/useDependencies.hook";
import type { VisibleDependency } from "../../hooks/useRenderDependency.hook";
import type { HandleStopWorkingProps } from "../../hooks/useWorkingActions";
import type { Node, Edge } from "./utils/createNodes";

export enum LayoutModes {
  FIXED = "fixed-layout",
  FREE = "free-layout",
  PERFORMANCE = "performance-mode",
  HIERARCHY = "hierarchy"
}

export enum DependencyGraphTypes {
  "RULE" = "RULE",
  "LEVEL_1" = "LEVEL 1",
  "LEVEL_N" = "LEVEL N"
}

export enum TemplateImportStatusTypes {
  "NOT_STARTED" = "NOT_STARTED",
  "STARTED" = "STARTED",
  "COMPLETED" = "COMPLETED",
  "CANCELLED" = "CANCELLED"
}

export type DependencyGraphData = {
  nodeId: string | null;
  graphType: DependencyGraphTypes | null;
};

export type DropPoint = {
  fieldIndex?: number;
  stepIndex?: number;
  stepgroupIndex?: number;
  stepgroupId?: string;
  stepId?: string;
  stepLevel?: number;
  positionInLevel?: number;
} | null;

type ProcessEditorContextType = {
  packageId: number;
  branchId: number;
  savepointId: number;
  processId: string;
  nodes: Node[];
  setNodes: Dispatch<SetStateAction<any>>;
  edges: Edge[];
  setEdges: Dispatch<SetStateAction<Edge[]>>;
  toggleCollapsedTag: (tag: string) => void;
  isReadOnly: boolean;
  dependencies: Dependency[];
  visibleDependencies: VisibleDependency[];
  setVisibleDependencies: Dispatch<SetStateAction<VisibleDependency[]>>;
  dependencyGraphData: DependencyGraphData;
  setDependencyGraphData: Dispatch<DependencyGraphData>;
  isSearchVisible: boolean;
  setIsSearchVisible: Dispatch<SetStateAction<boolean>>;
  isAddingNewItemDisabled: boolean;
  setIsAddingNewItemDisabled: Dispatch<SetStateAction<boolean>>;
  dropPoint: DropPoint;
  setDropPoint: Dispatch<SetStateAction<DropPoint>>;
  refreshMetaJSON: VoidFunction;
  workingActionOptions: {
    isLoading: boolean;
    handleStartWorking: () => Promise<void>;
    handleStopWorking: ({ type }: HandleStopWorkingProps) => Promise<void>;
  };
  layoutMode: LayoutModes;
  setLayoutMode: Dispatch<SetStateAction<LayoutModes>>;
  undoRedoFeedbackMsgType: FeedbackMessageType;
  setUndoRedoFeedbackMsgType: (type: FeedbackMessageType) => void;
  isHeyCertaInContext: boolean;
  selectedComponentNode: Node | null | undefined;
};

const ProcessEditorContext = React.createContext<
  ProcessEditorContextType | undefined
>(undefined);

export const ProcessEditorContextProvider: FC<{
  value: ProcessEditorContextType;
  children: ReactNode;
}> = ({ value, ...props }) => {
  return (
    <ProcessEditorContext.Provider value={value}>
      {props.children}
    </ProcessEditorContext.Provider>
  );
};

export const useProcessEditorContext = () => {
  const contextValue = useContext(ProcessEditorContext);
  if (typeof contextValue === "undefined") {
    throw new Error("No value defined for the processEditor context");
  }
  return contextValue;
};
